import * as React from 'react';

import type * as api from '@gitbook/api-client';
import type * as analytics from '@gitbook/app-analytics/client';
import type { NavigationScreenSpaceState } from '@gitbook/app-navigation';
import { SymbolsTable } from '@gitbook/expr';
import { getVariableValueType, type Revision } from '@gitbook/hive-core';
import { useHiveRevision } from '@gitbook/hive-react';
import { useScreenType } from '@gitbook/spine-react';

import {
    ExpressionProvider,
    SiteVisitorExpressionContextProvider,
    SpaceAdaptiveContextProvider,
} from '../../../Components';
import { NavigationRedirect } from '../../../navigation';
import { useMarkContentAsRecentlyVisited } from '../../../state';
import { WithConflictsModal } from '../Conflicts';
import { DiffContextProvider } from '../Diff';
import { SpaceRevisionContent } from './SpaceRevisionContent';

interface SpaceContentBaseProps {
    space: api.Space;
    changeRequest: api.ChangeRequest | null | undefined;
    analyticsContext: analytics.AnalyticsContextSpaceContent;
    readOnly: boolean;
}

export interface SpaceContentProps extends SpaceContentBaseProps {
    screen: NavigationScreenSpaceState;
}

/**
 * Render the content of a space after it has been fetched by the parent component.
 */
export function SpaceContent(props: SpaceContentProps): React.ReactElement {
    const { space, screen, changeRequest, analyticsContext, readOnly } = props;

    // Save space ID in local storage as recently visited
    useMarkContentAsRecentlyVisited({ contentId: space.id, organizationId: space.organization });

    const { revision, sourceRevision } = useHiveRevision();
    const { isMobile } = useScreenType();

    // When a revision has conflicts we do not permit the diff/preview view and redirect the user
    if (screen.mode && revision.conflicts) {
        return <NavigationRedirect to={{ screen: { mode: undefined } }} replace />;
    }

    // We do not display the comments inline on mobile, we show the comments sidesheet instead
    if (screen.sideSheet?.type === 'comments' && isMobile) {
        return (
            <NavigationRedirect
                to={{
                    screen: { sideSheet: { type: 'comments', comment: screen.sideSheet.comment } },
                }}
                replace
            />
        );
    }

    return (
        <SiteVisitorExpressionContextProvider space={space}>
            <RevisionExpressionContext revision={revision}>
                <DiffContextProvider
                    screen={screen}
                    contentId={space.id}
                    changeRequest={changeRequest || undefined}
                    revision={revision}
                    sourceRevision={sourceRevision}
                >
                    <SpaceAdaptiveContextProvider
                        organizationId={space.organization}
                        spaceId={space.id}
                    >
                        <SpaceRevisionContent
                            space={space}
                            changeRequest={changeRequest || undefined}
                            readOnly={readOnly}
                            withSidePanel={!!screen.sideSheet}
                            screen={screen}
                            analyticsContext={analyticsContext}
                        />
                    </SpaceAdaptiveContextProvider>

                    <WithConflictsModal revision={revision} />
                </DiffContextProvider>
            </RevisionExpressionContext>
        </SiteVisitorExpressionContextProvider>
    );
}

/**
 * Provide the context for dynamic expressions in this space.
 */
function RevisionExpressionContext(props: React.PropsWithChildren<{ revision: Revision }>) {
    const { revision } = props;

    const expressionContext = React.useMemo(() => {
        return {
            inputValues: {
                space: {
                    vars: revision.variables ?? {},
                },
            },
            symbolsTable: new SymbolsTable({
                space: SymbolsTable.inferSymbolFromJSONSchema(
                    {
                        type: 'object',
                        description: 'The attributes tied to the current space.',
                        properties: {
                            vars: {
                                type: 'object',
                                description: 'The variables of the space.',
                                properties: Object.fromEntries(
                                    Object.entries(revision.variables ?? {}).map(([key, value]) => [
                                        key,
                                        {
                                            type: getVariableValueType(value),
                                            description: `The value of the variable ${key}.`,
                                        },
                                    ])
                                ),
                            },
                        },
                        required: ['vars'],
                    },
                    'space'
                ),
            }),
        };
    }, [revision.variables]);

    return <ExpressionProvider {...expressionContext}>{props.children}</ExpressionProvider>;
}
